import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, ShieldCheck, ShieldX, ShieldAlert, Clock, User,
  CheckCircle, XCircle, AlertCircle, RefreshCw, ChevronDown,
  ChevronUp, Eye, Calendar, Activity, FileText, ExternalLink,
  Timer, Ban, History, ClipboardList
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function SupportAccessPanel() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // pending, active, history
  const [selectedSession, setSelectedSession] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Grant modal state
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantSession, setGrantSession] = useState(null);
  const [grantSettings, setGrantSettings] = useState({
    duration_hours: 4,
    mask_financials: true,
    access_level: null
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Revoke modal state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeSession, setRevokeSession] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const [allRes, pendingRes, activeRes] = await Promise.all([
        api.get('/support/sessions?limit=100'),
        api.get('/support/sessions/pending'),
        api.get('/support/sessions/active')
      ]);

      setSessions(allRes.data.sessions || []);
      setPendingSessions(pendingRes.data.sessions || []);
      setActiveSessions(activeRes.data.sessions || []);
    } catch (err) {
      console.error('Error fetching support sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchSessions();
    }
  }, [user, fetchSessions]);

  const fetchAuditLog = async (sessionId) => {
    try {
      setAuditLoading(true);
      const response = await api.get(`/support/sessions/${sessionId}/audit`);
      setAuditLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const openGrantModal = (session) => {
    setGrantSession(session);
    setGrantSettings({
      duration_hours: session.duration_hours || 4,
      mask_financials: true,
      access_level: session.access_level
    });
    setActionError('');
    setShowGrantModal(true);
  };

  const closeGrantModal = () => {
    setShowGrantModal(false);
    setGrantSession(null);
    setActionError('');
  };

  const handleGrant = async () => {
    if (!grantSession) return;

    try {
      setActionLoading(true);
      setActionError('');
      await api.post(`/support/sessions/${grantSession.id}/grant`, grantSettings);
      setActionSuccess('Support access granted successfully');
      closeGrantModal();
      fetchSessions();
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      console.error('Error granting access:', err);
      setActionError(err.response?.data?.detail || 'Failed to grant access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async (sessionId) => {
    if (!window.confirm('Are you sure you want to deny this support access request?')) {
      return;
    }

    try {
      setActionLoading(true);
      setActionError('');
      await api.post(`/support/sessions/${sessionId}/deny`);
      setActionSuccess('Support access denied');
      fetchSessions();
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      console.error('Error denying access:', err);
      setActionError(err.response?.data?.detail || 'Failed to deny access');
    } finally {
      setActionLoading(false);
    }
  };

  const openRevokeModal = (session) => {
    setRevokeSession(session);
    setRevokeReason('');
    setActionError('');
    setShowRevokeModal(true);
  };

  const closeRevokeModal = () => {
    setShowRevokeModal(false);
    setRevokeSession(null);
    setRevokeReason('');
    setActionError('');
  };

  const handleRevoke = async () => {
    if (!revokeSession) return;

    try {
      setActionLoading(true);
      setActionError('');
      await api.post(`/support/sessions/${revokeSession.id}/revoke`, {
        reason: revokeReason || undefined
      });
      setActionSuccess('Support access revoked');
      closeRevokeModal();
      fetchSessions();
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      console.error('Error revoking access:', err);
      setActionError(err.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setActionLoading(false);
    }
  };

  const viewAuditLog = (session) => {
    setSelectedSession(session);
    setShowAuditLog(true);
    fetchAuditLog(session.id);
  };

  const closeAuditLog = () => {
    setShowAuditLog(false);
    setSelectedSession(null);
    setAuditLogs([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    if (!expiresAt) return 'N/A';
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', icon: Clock },
      active: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: ShieldCheck },
      expired: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', icon: Timer },
      revoked: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: Ban },
      completed: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: CheckCircle },
      denied: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: XCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}>
        <Icon size={12} className="mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getAccessLevelLabel = (level) => {
    const labels = {
      standard: 'Standard (Full UI with masking)',
      readonly: 'Read Only (View only)',
      logs_only: 'Logs Only (System logs only)'
    };
    return labels[level] || level;
  };

  // Only show to admins
  if (user?.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 mt-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="animate-spin text-virgo-teal-400 mr-2" size={20} />
          <span className="text-virgo-teal-200">Loading support sessions...</span>
        </div>
      </div>
    );
  }

  const historyCount = sessions.filter(s => !['pending', 'active'].includes(s.status)).length;

  return (
    <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 shadow-lg mt-6">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h2 className="text-xl font-semibold text-virgo-teal-100 flex items-center">
          <Shield className="mr-2 text-cyan-400" size={20} />
          IT Support Access
          {pendingSessions.length > 0 && (
            <span className="ml-3 px-2 py-0.5 text-xs bg-yellow-500 text-yellow-900 rounded-full font-bold">
              {pendingSessions.length} pending
            </span>
          )}
        </h2>
        <button className="text-virgo-teal-400 hover:text-virgo-teal-200">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {expanded && (
        <>
          {/* Info Banner */}
          <div className="mt-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
            <div className="flex items-start">
              <ShieldAlert className="text-cyan-400 mr-3 mt-0.5 flex-shrink-0" size={18} />
              <div>
                <p className="text-cyan-300 text-sm font-medium">Remote Support Management</p>
                <p className="text-cyan-400/80 text-xs mt-1">
                  Manage support access requests from Astraea.AI staff. All client data is automatically masked during support sessions.
                </p>
              </div>
            </div>
          </div>

          {/* Success/Error Messages */}
          {actionSuccess && (
            <div className="mt-4 p-3 bg-green-500/20 border border-green-500/30 text-green-400 rounded-lg flex items-center">
              <CheckCircle size={18} className="mr-2" />
              {actionSuccess}
            </div>
          )}
          {actionError && (
            <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg flex items-center">
              <AlertCircle size={18} className="mr-2" />
              {actionError}
            </div>
          )}

          {/* Tabs */}
          <div className="mt-4 flex border-b border-virgo-navy-600">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === 'pending'
                  ? 'border-yellow-500 text-yellow-400'
                  : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200'
              }`}
            >
              <Clock size={14} className="inline mr-1.5" />
              Pending ({pendingSessions.length})
            </button>
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === 'active'
                  ? 'border-green-500 text-green-400'
                  : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200'
              }`}
            >
              <ShieldCheck size={14} className="inline mr-1.5" />
              Active ({activeSessions.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === 'history'
                  ? 'border-virgo-teal-500 text-virgo-teal-300'
                  : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200'
              }`}
            >
              <History size={14} className="inline mr-1.5" />
              History ({historyCount})
            </button>
            <div className="flex-1" />
            <button
              onClick={fetchSessions}
              className="px-3 py-2 text-virgo-teal-400 hover:text-virgo-teal-200 transition"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {/* Pending Requests */}
            {activeTab === 'pending' && (
              <div>
                {pendingSessions.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="mx-auto text-virgo-teal-600 mb-3" size={40} />
                    <p className="text-virgo-teal-400">No pending support requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingSessions.map(session => (
                      <div key={session.id} className="bg-virgo-navy-700 rounded-lg p-4 border border-yellow-500/30">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start">
                            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center mr-3">
                              <User className="text-yellow-400" size={20} />
                            </div>
                            <div>
                              <h4 className="font-medium text-virgo-teal-100">
                                {session.support_user_name || session.support_user_id}
                              </h4>
                              {session.support_user_email && (
                                <p className="text-xs text-virgo-teal-500">{session.support_user_email}</p>
                              )}
                              <p className="text-sm text-virgo-teal-300 mt-1">{session.request_reason}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-virgo-teal-500">
                                <span className="flex items-center">
                                  <Calendar size={12} className="mr-1" />
                                  {formatDate(session.requested_at)}
                                </span>
                                <span className="flex items-center">
                                  <Timer size={12} className="mr-1" />
                                  Requested: {session.duration_hours}h
                                </span>
                                {session.ticket_id && (
                                  <span className="flex items-center">
                                    <FileText size={12} className="mr-1" />
                                    Ticket: {session.ticket_id}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openGrantModal(session)}
                              disabled={actionLoading}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition text-sm font-medium flex items-center disabled:opacity-50"
                            >
                              <CheckCircle size={16} className="mr-1.5" />
                              Grant
                            </button>
                            <button
                              onClick={() => handleDeny(session.id)}
                              disabled={actionLoading}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition text-sm font-medium flex items-center disabled:opacity-50"
                            >
                              <XCircle size={16} className="mr-1.5" />
                              Deny
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Active Sessions */}
            {activeTab === 'active' && (
              <div>
                {activeSessions.length === 0 ? (
                  <div className="text-center py-8">
                    <ShieldCheck className="mx-auto text-virgo-teal-600 mb-3" size={40} />
                    <p className="text-virgo-teal-400">No active support sessions</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeSessions.map(session => (
                      <div key={session.id} className="bg-virgo-navy-700 rounded-lg p-4 border border-green-500/30">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start">
                            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mr-3">
                              <ShieldCheck className="text-green-400" size={20} />
                            </div>
                            <div>
                              <h4 className="font-medium text-virgo-teal-100">
                                {session.support_user_name || session.support_user_id}
                              </h4>
                              {session.support_user_email && (
                                <p className="text-xs text-virgo-teal-500">{session.support_user_email}</p>
                              )}
                              <p className="text-sm text-virgo-teal-300 mt-1">{session.request_reason}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-virgo-teal-500">
                                <span className="flex items-center text-green-400">
                                  <Activity size={12} className="mr-1" />
                                  {formatTimeRemaining(session.expires_at)}
                                </span>
                                <span className="flex items-center">
                                  <Shield size={12} className="mr-1" />
                                  {getAccessLevelLabel(session.access_level)}
                                </span>
                                {session.mask_financials && (
                                  <span className="text-cyan-400">Financials masked</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => viewAuditLog(session)}
                              className="px-3 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 transition text-sm flex items-center"
                            >
                              <ClipboardList size={16} className="mr-1.5" />
                              View Log
                            </button>
                            <button
                              onClick={() => openRevokeModal(session)}
                              disabled={actionLoading}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition text-sm font-medium flex items-center disabled:opacity-50"
                            >
                              <Ban size={16} className="mr-1.5" />
                              Revoke
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Session History */}
            {activeTab === 'history' && (
              <div>
                {historyCount === 0 ? (
                  <div className="text-center py-8">
                    <History className="mx-auto text-virgo-teal-600 mb-3" size={40} />
                    <p className="text-virgo-teal-400">No session history</p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-virgo-navy-600">
                    <table className="min-w-full">
                      <thead className="bg-virgo-navy-700">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase">Support Staff</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase">Status</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase">Requested</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase">Ended</th>
                          <th className="text-center py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-virgo-navy-700">
                        {sessions
                          .filter(s => !['pending', 'active'].includes(s.status))
                          .map(session => (
                            <tr key={session.id} className="hover:bg-virgo-navy-700/50">
                              <td className="py-3 px-4">
                                <div className="text-sm text-virgo-teal-100">
                                  {session.support_user_name || session.support_user_id}
                                </div>
                                {session.ticket_id && (
                                  <div className="text-xs text-virgo-teal-500">
                                    Ticket: {session.ticket_id}
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                {getStatusBadge(session.status)}
                              </td>
                              <td className="py-3 px-4 text-sm text-virgo-teal-300">
                                {formatDate(session.requested_at)}
                              </td>
                              <td className="py-3 px-4 text-sm text-virgo-teal-300">
                                {formatDate(session.ended_at)}
                                {session.ended_by && (
                                  <span className="text-xs text-virgo-teal-500 ml-1">
                                    by {session.ended_by}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <button
                                  onClick={() => viewAuditLog(session)}
                                  className="text-virgo-teal-400 hover:text-virgo-teal-200 transition"
                                  title="View Audit Log"
                                >
                                  <Eye size={18} />
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Grant Access Modal */}
      {showGrantModal && grantSession && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={closeGrantModal}>
          <div className="bg-virgo-navy-800 rounded-lg p-6 max-w-md w-full mx-4 border border-virgo-navy-600 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center mb-4">
              <ShieldCheck className="text-green-400 mr-3" size={24} />
              <h3 className="text-lg font-semibold text-virgo-teal-100">Grant Support Access</h3>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4 mb-4">
              <p className="text-sm text-virgo-teal-300">
                <strong className="text-virgo-teal-100">{grantSession.support_user_name || grantSession.support_user_id}</strong>
              </p>
              <p className="text-xs text-virgo-teal-500 mt-1">{grantSession.request_reason}</p>
            </div>

            {actionError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-sm">
                {actionError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">
                  Session Duration
                </label>
                <select
                  value={grantSettings.duration_hours}
                  onChange={(e) => setGrantSettings(prev => ({ ...prev, duration_hours: parseInt(e.target.value) }))}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                >
                  <option value={1}>1 hour</option>
                  <option value={2}>2 hours</option>
                  <option value={4}>4 hours</option>
                  <option value={8}>8 hours</option>
                  <option value={24}>24 hours</option>
                </select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-virgo-teal-100">Mask Financial Data</p>
                  <p className="text-xs text-virgo-teal-500">Hide amounts, rates, and billing info</p>
                </div>
                <button
                  type="button"
                  onClick={() => setGrantSettings(prev => ({ ...prev, mask_financials: !prev.mask_financials }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    grantSettings.mask_financials ? 'bg-virgo-teal-600' : 'bg-gray-500'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      grantSettings.mask_financials ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closeGrantModal}
                className="flex-1 px-4 py-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleGrant}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition font-medium flex items-center justify-center disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <RefreshCw className="animate-spin mr-2" size={16} />
                    Granting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Grant Access
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Access Modal */}
      {showRevokeModal && revokeSession && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={closeRevokeModal}>
          <div className="bg-virgo-navy-800 rounded-lg p-6 max-w-md w-full mx-4 border border-virgo-navy-600 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center mb-4">
              <ShieldX className="text-red-400 mr-3" size={24} />
              <h3 className="text-lg font-semibold text-virgo-teal-100">Revoke Support Access</h3>
            </div>

            <div className="bg-virgo-navy-700 rounded-lg p-4 mb-4">
              <p className="text-sm text-virgo-teal-300">
                <strong className="text-virgo-teal-100">{revokeSession.support_user_name || revokeSession.support_user_id}</strong>
              </p>
              <p className="text-xs text-virgo-teal-500 mt-1">
                Session expires: {formatDate(revokeSession.expires_at)}
              </p>
            </div>

            <p className="text-sm text-virgo-teal-400 mb-4">
              This will immediately end the support session. The support staff will no longer be able to access your Virgo instance.
            </p>

            {actionError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-sm">
                {actionError}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-100 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="Why are you revoking access?"
                rows={3}
                className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100 placeholder-virgo-teal-600 resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeRevokeModal}
                className="flex-1 px-4 py-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition font-medium flex items-center justify-center disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <RefreshCw className="animate-spin mr-2" size={16} />
                    Revoking...
                  </>
                ) : (
                  <>
                    <Ban size={16} className="mr-2" />
                    Revoke Access
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditLog && selectedSession && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={closeAuditLog}>
          <div className="bg-virgo-navy-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-virgo-navy-600 shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ClipboardList className="text-virgo-teal-400 mr-3" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-virgo-teal-100">Session Audit Log</h3>
                  <p className="text-xs text-virgo-teal-500">
                    {selectedSession.support_user_name || selectedSession.support_user_id}
                  </p>
                </div>
              </div>
              {getStatusBadge(selectedSession.status)}
            </div>

            <div className="flex-1 overflow-auto">
              {auditLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="animate-spin text-virgo-teal-400 mr-2" size={20} />
                  <span className="text-virgo-teal-200">Loading audit log...</span>
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="mx-auto text-virgo-teal-600 mb-3" size={40} />
                  <p className="text-virgo-teal-400">No audit entries found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map(log => (
                    <div key={log.id} className="bg-virgo-navy-700 rounded-lg p-3 text-sm">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="font-medium text-virgo-teal-100">{log.action}</span>
                          {log.resource_type && (
                            <span className="text-virgo-teal-500 ml-2">
                              ({log.resource_type}{log.resource_id ? `: ${log.resource_id}` : ''})
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-virgo-teal-500">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-virgo-teal-500">
                        <span className="font-mono">{log.method} {log.path}</span>
                        {log.status_code && (
                          <span className={`ml-2 ${log.status_code < 400 ? 'text-green-400' : 'text-red-400'}`}>
                            [{log.status_code}]
                          </span>
                        )}
                        {log.duration_ms && (
                          <span className="ml-2">{log.duration_ms}ms</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-virgo-navy-600">
              <button
                onClick={closeAuditLog}
                className="w-full px-4 py-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportAccessPanel;
